import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  Snackbar,
  Checkbox,
  ListItemText,
  Divider,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import SearchIcon from '@mui/icons-material/Search'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import { fetchAllRiderVehicleHistory, fetchClientNamesFromHistory, exportAllRiderVehicleHistory } from '../services'
import {
  calculateVehicleRent,
  updateVehicleRentAmount,
  VehicleRentUpdateError,
} from '../services/vehicleHistoryService'
import VehicleRentUpdateItems from './VehicleRentUpdateItems'
import * as XLSX from 'xlsx'

interface VehicleHistoryRecord {
  riderId: string
  riderPublicId: string
  riderName: string
  clientName?: string
  registrationNumber: string
  status: 'ACTIVE' | 'RETURNED' | 'CLIENT_SWAP'
  assignedAt: string
  returnedAt: string | null
  vehicleAgeDays: string
}

const RiderVehicleHistory: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [rows, setRows] = useState<VehicleHistoryRecord[]>([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState('')
  const [showExportDialog, setShowExportDialog] = useState(false)
  const [exportMode, setExportMode] = useState<'specific' | 'all'>('specific')
  const [selectedClientNames, setSelectedClientNames] = useState<string[]>([])
  const [showCalculateRentDialog, setShowCalculateRentDialog] = useState(false)
  const [showUpdateRentDialog, setShowUpdateRentDialog] = useState(false)
  const [showRentDataAudit, setShowRentDataAudit] = useState(false)
  const [rentUpdateFile, setRentUpdateFile] = useState<File | null>(null)
  const [updatingRent, setUpdatingRent] = useState(false)
  const [rentUpdateError, setRentUpdateError] = useState<VehicleRentUpdateError | null>(null)
  const [calculating, setCalculating] = useState(false)
  const [weekStartDate, setWeekStartDate] = useState<string>('')
  const [weekEndDate, setWeekEndDate] = useState<string>('')
  const [rentError, setRentError] = useState<string | null>(null)
  const [clientNamesFromApi, setClientNamesFromApi] = useState<string[]>([])
  const [clientNamesLoading, setClientNamesLoading] = useState(false)
  const navigate = useNavigate()

  const loadPage = async (p = page, limit = rowsPerPage) => {
    try {
      setLoading(true)
      setError(null)
      const offset = p * limit
      const res = await fetchAllRiderVehicleHistory({ limit, offset })

      if (res.success && res.data?.data) {
        const items = Array.isArray(res.data.data) ? res.data.data : []
        setRows(items)
        setTotal(res.data.pagination?.total || 0)
      } else {
        setError(res.message || 'Failed to load vehicle history')
        setRows([])
        setTotal(0)
      }
    } catch (e: any) {
      console.error('Error loading vehicle history:', e)
      setError(e.message || 'An error occurred while loading vehicle history')
      setRows([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }

  const handleExportClick = () => {
    setShowExportDialog(true)
  }

  const handleExportConfirm = async () => {
    try {
      setExporting(true)
      setError(null)

      const params: any = {}
      if (exportMode === 'specific') {
        if (selectedClientNames.length === 0) {
          setError('Please select one or more Client Names')
          setExporting(false)
          return
        }
        params.clientName = selectedClientNames
      }
      // else: exportMode === 'all' means no clientName param, export all

      const res = await exportAllRiderVehicleHistory(params)

      if (res.success && res.data?.blob) {
        const url = URL.createObjectURL(res.data.blob)
        const a = document.createElement('a')
        a.href = url
        a.download = res.data.filename || 'vehicle-history.xlsx'
        document.body.appendChild(a)
        a.click()
        a.remove()
        URL.revokeObjectURL(url)
        setShowExportDialog(false)
      } else {
        setError(res.message || 'Export failed')
      }
    } catch (e: any) {
      console.error('Export failed:', e)
      setError(e.message || 'An error occurred during export')
    } finally {
      setExporting(false)
    }
  }

  const handleExportCancel = () => {
    setShowExportDialog(false)
    setExportMode('specific')
    setSelectedClientNames([])
  }

  const handleCalculateRentClick = () => {
    setShowCalculateRentDialog(true)
    setRentError(null)
    setWeekStartDate('')
    setWeekEndDate('')
  }

  const handleUpdateRentClick = () => {
    setRentUpdateFile(null)
    setRentUpdateError(null)
    setShowUpdateRentDialog(true)
  }

  const handleRentUpdateFileChange = (file?: File) => {
    setRentUpdateError(null)
    if (!file) {
      setRentUpdateFile(null)
      return
    }
    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      setRentUpdateFile(null)
      setRentUpdateError({
        message: 'Select an Excel workbook with an .xlsx or .xls extension.',
      })
      return
    }
    if (file.size > 10 * 1024 * 1024) {
      setRentUpdateFile(null)
      setRentUpdateError({
        message: 'The selected file exceeds the 10 MB upload limit.',
      })
      return
    }
    setRentUpdateFile(file)
  }

  const handleRentUpdateUpload = async () => {
    if (!rentUpdateFile) {
      setRentUpdateError({ message: 'Choose a rent calculation Excel file to upload.' })
      return
    }

    setUpdatingRent(true)
    setRentUpdateError(null)
    try {
      const result = await updateVehicleRentAmount(rentUpdateFile)
      if (result.success === false) {
        setRentUpdateError(result.error)
        return
      }

      setShowUpdateRentDialog(false)
      setRentUpdateFile(null)
      setSuccess(
        `Rent updated: ₹${Number(result.data.totalRentAmount).toLocaleString('en-IN')} added for ${result.data.updatedRiders} rider(s).`,
      )
    } catch (e: any) {
      console.error('Rent amount upload failed:', e)
      setRentUpdateError({
        message: e.message || 'Unable to upload the rent calculation file.',
      })
    } finally {
      setUpdatingRent(false)
    }
  }

  const formatDateForApi = (dateStr: string): string => {
    // dateStr format: YYYY-MM-DD (from date input)
    // API expects: DD/MM/YYYY
    if (!dateStr) return ''
    const [year, month, day] = dateStr.split('-')
    return `${day}/${month}/${year}`
  }

  const handleCalculateRentConfirm = async () => {
    if (!weekStartDate) {
      setRentError('Please select a week start date')
      return
    }
    if (!weekEndDate) {
      setRentError('Please select a week end date')
      return
    }

    // Validate date range
    const startDate = new Date(weekStartDate)
    const endDate = new Date(weekEndDate)
    if (startDate >= endDate) {
      setRentError('Week start date must be before week end date')
      return
    }

    try {
      setCalculating(true)
      setRentError(null)

      const startDateFormatted = formatDateForApi(weekStartDate)
      const endDateFormatted = formatDateForApi(weekEndDate)

      // First export the vehicle history sheet (specific client or all) and then pass it to the calculate API
      const params: any = {}
      if (exportMode === 'specific') {
        if (selectedClientNames.length === 0) {
          setRentError('Please select one or more Client Names')
          setCalculating(false)
          return
        }
        params.clientName = selectedClientNames
      }

      const exportRes = await exportAllRiderVehicleHistory(params)
      if (!exportRes.success || !exportRes.data?.blob) {
        setRentError(exportRes.message || 'Failed to export vehicle history for rent calculation')
        setCalculating(false)
        return
      }

      const blob = exportRes.data.blob
      const filename = exportRes.data.filename || 'vehicle-history.xlsx'
      // Load workbook and fill empty ReturnedAt cells with today's date (DD/MM/YYYY)
      let res: any = null
      try {
        const arrayBuffer = await blob.arrayBuffer()
        const workbook = XLSX.read(arrayBuffer, { type: 'array' })
        const today = new Date()
        const dd = String(today.getDate()).padStart(2, '0')
        const mm = String(today.getMonth() + 1).padStart(2, '0')
        const yyyy = String(today.getFullYear())
        const todayStr = `${dd}/${mm}/${yyyy}`

        for (const sheetName of workbook.SheetNames) {
          const ws = workbook.Sheets[sheetName]
          if (!ws) continue
          const rows = XLSX.utils.sheet_to_json<any[]>(ws, { header: 1 })
          if (!rows || rows.length === 0) continue
          const headerRow = rows[0] as any[]
          const colIndex = headerRow.findIndex((h) => String(h).trim() === 'ReturnedAt')
          if (colIndex === -1) continue
          // Fill empty cells in this column
          for (let r = 1; r < rows.length; r++) {
            const cellVal = rows[r][colIndex]
            if (cellVal === undefined || cellVal === null || String(cellVal).trim() === '') {
              rows[r][colIndex] = todayStr
            }
          }
          const newWs = XLSX.utils.aoa_to_sheet(rows)
          workbook.Sheets[sheetName] = newWs
        }

        const outBuf = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
        const newBlob = new Blob([outBuf], { type: blob.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const file = new File([newBlob], filename, { type: newBlob.type })

        res = await calculateVehicleRent(file, startDateFormatted, endDateFormatted)
        
        // replace blob for possible debugging/use later
        // exportRes.data.blob = newBlob
        
        if (res.success && res.data?.blob) {
          // continue below to handle download
        } else {
          setRentError(res.message || 'Failed to calculate rent')
          setCalculating(false)
          return
        }
      } catch (e: any) {
        console.error('Error processing workbook:', e)
        setRentError(e.message || 'Failed to process exported Excel file')
        setCalculating(false)
        return
      }

      if (res.success && res.data?.blob) {
        // Download the file
        const url = URL.createObjectURL(res.data.blob)
        const a = document.createElement('a')
        a.href = url
        a.download = res.data.filename || 'vehicle-rent-calculation.xlsx'
        document.body.appendChild(a)
        a.click()
        a.remove()
        URL.revokeObjectURL(url)

        setSuccess('Vehicle rent calculation completed successfully! File downloaded.')
        setShowCalculateRentDialog(false)
        setWeekStartDate('')
        setWeekEndDate('')
        
        // Close success message after 5 seconds
        setTimeout(() => setSuccess(null), 5000)
      } else {
        setRentError(res.message || 'Failed to calculate rent')
      }
    } catch (e: any) {
      console.error('Calculate rent error:', e)
      setRentError(e.message || 'An error occurred during rent calculation')
    } finally {
      setCalculating(false)
    }
  }

  const handleCalculateRentCancel = () => {
    setShowCalculateRentDialog(false)
    setRentError(null)
    setExportMode('specific')
    setWeekStartDate('')
    setWeekEndDate('')
  }

  const loadClientNames = async () => {
    try {
      setClientNamesLoading(true)
      const res = await fetchClientNamesFromHistory()
      if (res.success) {
        const names = Array.isArray(res.data)
          ? res.data
          : []
        setClientNamesFromApi([...new Set(names.map((name) => String(name).trim()).filter(Boolean))].sort())
      } else {
        const fallbackNames = [...new Set(rows.filter(r => r.clientName && r.clientName.trim()).map(r => r.clientName))]
        setClientNamesFromApi(fallbackNames.sort())
      }
    } catch (e: any) {
      console.error('Failed to fetch client names from api:', e)
      const fallbackNames = [...new Set(rows.filter(r => r.clientName && r.clientName.trim()).map(r => r.clientName))]
      setClientNamesFromApi(fallbackNames.sort())
    } finally {
      setClientNamesLoading(false)
    }
  }

  const uniqueClientNames = React.useMemo(() => {
    if (clientNamesFromApi.length > 0) {
      return clientNamesFromApi
    }

    const names = [...new Set(rows.filter(r => r.clientName && r.clientName.trim()).map(r => r.clientName))]
    return names.sort()
  }, [clientNamesFromApi, rows])

  useEffect(() => {
    loadPage(page, rowsPerPage)
  }, [page, rowsPerPage])

  useEffect(() => {
    loadClientNames()
  }, [])

  //Search filter applied on the client side for better performance and user experience

  const filteredRows = React.useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) => {
      const reg = (r.registrationNumber || '').toLowerCase()
      const pub = (r.riderPublicId || '').toLowerCase()
      const id = (r.riderId || '').toLowerCase()
      return reg.includes(q) || pub.includes(q) || id.includes(q)
    })
  }, [rows, search])

  const displayedRows = React.useMemo(() => {
    // The backend already returns the correct page slice for the given offset/limit,
    // so do not slice it again on the client. That causes page 2 to become empty.
    return filteredRows
  }, [filteredRows])

  const getChipColor = (status: string) => {
    if (status === 'ACTIVE') return 'success'
    if (status === 'CLIENT_SWAP') return 'warning'
    return 'default'
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Rider Vehicle History
      </Typography>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button variant="text" startIcon={<ArrowBackIcon />} onClick={() => navigate('/mis')}>Back</Button>
            <Typography variant="subtitle1">
              Export and preview rider vehicle assignment history
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<FactCheckOutlinedIcon />}
              onClick={() => setShowRentDataAudit(true)}
            >
              Rent Data Audit
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={handleExportClick}
              disabled={exporting || loading}
            >
              {exporting ? 'Exporting...' : 'Download XLSX'}
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<CloudUploadIcon />}
              onClick={handleCalculateRentClick}
              disabled={calculating}
            >
              {calculating ? 'Calculating...' : 'Calculate Rent'}
            </Button>
            <Button
              variant="contained"
              color="warning"
              startIcon={<UploadFileIcon />}
              onClick={handleUpdateRentClick}
              disabled={updatingRent}
            >
              Update Rent Amount
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={showRentDataAudit}
        onClose={() => setShowRentDataAudit(false)}
        maxWidth="xl"
        fullWidth
        fullScreen={false}
        PaperProps={{ sx: { maxHeight: '92vh' } }}
      >
        <DialogContent sx={{ p: 0, overflowY: 'auto' }}>
          <VehicleRentUpdateItems onClose={() => setShowRentDataAudit(false)} />
        </DialogContent>
      </Dialog>

      <Card>
        <CardContent>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <TextField
                  size="small"
                  placeholder="Search by Registration or Rider ID"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value)
                    setPage(0)
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="left">
                    <strong>Rider ID</strong>
                  </TableCell>
                  <TableCell align="left">
                    <strong>Rider Name</strong>
                  </TableCell>
                  <TableCell align="left">
                    <strong>Registration</strong>
                  </TableCell>
                  <TableCell align="center">
                    <strong>Client Name</strong>
                  </TableCell>
                  <TableCell align="center">
                    <strong>Status</strong>
                  </TableCell>
                  <TableCell align="left">
                    <strong>Assigned At</strong>
                  </TableCell>
                  <TableCell align="left">
                    <strong>Returned At</strong>
                  </TableCell>
                  <TableCell align="center">
                    <strong>Utilized Days</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="textSecondary">
                        No vehicle history available
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="textSecondary">No matching records</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedRows.map((row, idx) => (
                    <TableRow key={`${row.riderId}-${row.assignedAt}-${idx}`}>
                      <TableCell>{row.riderPublicId || row.riderId}</TableCell>
                      <TableCell>{row.riderName || '-'}</TableCell>
                      <TableCell>{row.registrationNumber || '-'}</TableCell>
                      <TableCell align="center">{row.clientName || '-'}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={row.status}
                          color={getChipColor(row.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {row.assignedAt
                          ? String(row.assignedAt).trim().replace('T', ' ').replace(/Z$/i, '').split('.')[0].split(' ')[0]
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {row.returnedAt
                          ? String(row.returnedAt).trim().replace('T', ' ').replace(/Z$/i, '').split('.')[0].split(' ')[0]
                          : '-'}
                      </TableCell>
                      <TableCell align="center">
                        {row.vehicleAgeDays ?? '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => {
                setPage(newPage)
              }}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                const newRowsPerPage = Number(e.target.value)
                setRowsPerPage(newRowsPerPage)
                setPage(0)
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Export Dialog */}
      <Dialog open={showExportDialog} onClose={handleExportCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Export Vehicle History</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            How would you like to export the data?
          </Typography>
          <RadioGroup
            value={exportMode}
            onChange={(e) => {
              setExportMode(e.target.value as 'specific' | 'all')
              setSelectedClientNames([])
            }}
          >
            <FormControlLabel
              value="specific"
              control={<Radio />}
              label="Export for Specific Client(s)"
            />
            {exportMode === 'specific' && (
              <FormControl sx={{ ml: 4, mt: 2, minWidth: 280 }}>
                <InputLabel>Select Client Name(s) *</InputLabel>
                <Select
                  multiple
                  value={selectedClientNames}
                  label="Select Client Name(s) *"
                  onChange={(e) => setSelectedClientNames(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  renderValue={(selected) => (selected as string[]).join(', ')}
                >
                  {uniqueClientNames.map((clientName) => (
                    <MenuItem key={clientName} value={clientName}>
                      <Checkbox checked={selectedClientNames.indexOf(clientName) > -1} />
                      <ListItemText primary={clientName} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControlLabel
              value="all"
              control={<Radio />}
              label="Export All Data"
              sx={{ mt: 2 }}
            />
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleExportCancel} disabled={exporting}>
            Cancel
          </Button>
          <Button
            onClick={handleExportConfirm}
            variant="contained"
            disabled={exporting || (exportMode === 'specific' && selectedClientNames.length === 0)}
          >
            {exporting ? 'Exporting...' : 'Download'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Calculate Rent Dialog */}
      <Dialog open={showCalculateRentDialog} onClose={handleCalculateRentCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Calculate Vehicle Rent</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {rentError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRentError(null)}>
              {rentError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Export selection to auto-fetch file for rent calculation */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Select Data to Use for Calculation
              </Typography>
              <RadioGroup
                value={exportMode}
                onChange={(e) => {
                  setExportMode(e.target.value as 'specific' | 'all')
                  setSelectedClientNames([])
                }}
              >
                <FormControlLabel
                  value="specific"
                  control={<Radio />}
                  label="Use Specific Client(s)"
                />
                {exportMode === 'specific' && (
                  <FormControl sx={{ ml: 4, mt: 2, minWidth: 280 }}>
                    <InputLabel>Select Client Name(s) *</InputLabel>
                    <Select
                      multiple
                      value={selectedClientNames}
                      label="Select Client Name(s) *"
                      onChange={(e) => setSelectedClientNames(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                      renderValue={(selected) => (selected as string[]).join(', ')}
                    >
                      {uniqueClientNames.map((clientName) => (
                        <MenuItem key={clientName} value={clientName}>
                          <Checkbox checked={selectedClientNames.indexOf(clientName) > -1} />
                          <ListItemText primary={clientName} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <FormControlLabel
                  value="all"
                  control={<Radio />}
                  label="Use All Data"
                  sx={{ mt: 2 }}
                />
              </RadioGroup>
            </Box>

            {/* Week Start Date */}
            <Box>
              <TextField
                fullWidth
                label="Week Start Date *"
                type="date"
                value={weekStartDate}
                onChange={(e) => setWeekStartDate(e.target.value)}
                disabled={calculating}
                inputProps={{ max: weekEndDate || undefined }}
                InputLabelProps={{ shrink: true }}
                helperText="Select the start date of the week (DD/MM/YYYY)"
              />
            </Box>

            {/* Week End Date */}
            <Box>
              <TextField
                fullWidth
                label="Week End Date *"
                type="date"
                value={weekEndDate}
                onChange={(e) => setWeekEndDate(e.target.value)}
                disabled={calculating}
                inputProps={{ min: weekStartDate || undefined }}
                InputLabelProps={{ shrink: true }}
                helperText="Select the end date of the week (DD/MM/YYYY)"
              />
            </Box>

            {/* Loading State */}
            {calculating && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="textSecondary">
                  Calculating vehicle rent... Please wait.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCalculateRentCancel} disabled={calculating}>
            Cancel
          </Button>
          <Button
            onClick={handleCalculateRentConfirm}
            variant="contained"
            color="success"
            disabled={
              calculating || !weekStartDate || !weekEndDate ||
              (exportMode === 'specific' && selectedClientNames.length === 0)
            }
          >
            {calculating ? 'Calculating...' : 'Calculate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showUpdateRentDialog}
        onClose={() => {
          if (!updatingRent) {
            setShowUpdateRentDialog(false)
            setRentUpdateError(null)
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Upload Rent Calculation to Update Rent Amount</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="info">
              Upload the calculated rent workbook. Rent is added to the rider’s existing deficit rent and pending
              earnings. The same rider, client rider ID, and date range cannot be updated more than once.
            </Alert>

            <Typography variant="body2" color="text.secondary">
              The workbook must include RiderPublicID, RegistrationNumber, ClientRiderId, ClientName, weekStartDate,
              weekEndDate, numberOfDays, and totalRent. Dates must be in DD/MM/YYYY format.
            </Typography>

            {rentUpdateError && (
              <Alert
                severity="error"
                onClose={() => setRentUpdateError(null)}
                sx={{
                  alignItems: 'flex-start',
                  '& .MuiAlert-message': { width: '100%', minWidth: 0 },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'pre-line' }}>
                  {rentUpdateError.message}
                </Typography>

                {!!rentUpdateError.rowErrors?.length && (
                  <Box
                    component="ul"
                    sx={{ mt: 1, mb: 0, pl: 2.5, maxHeight: 240, overflowY: 'auto' }}
                  >
                    {rentUpdateError.rowErrors.map((rowError, index) => (
                      <li key={`${rowError.row}-${index}`}>
                        <Typography variant="body2">
                          Excel row {rowError.row}: {rowError.message}
                        </Typography>
                      </li>
                    ))}
                  </Box>
                )}

                {!!rentUpdateError.conflicts?.length && (
                  <Box
                    component="ul"
                    sx={{ mt: 1, mb: 0, pl: 2.5, maxHeight: 240, overflowY: 'auto' }}
                  >
                    {rentUpdateError.conflicts.map((conflict, index) => (
                      <li key={`${conflict.RiderPublicID}-${conflict.ClientRiderId}-${index}`}>
                        <Typography variant="body2">
                          Rider {conflict.RiderPublicID}, client rider {conflict.ClientRiderId}, dates{' '}
                          {conflict.weekStartDate}–{conflict.weekEndDate} overlap{' '}
                          {conflict.source === 'same-upload' ? 'another workbook row' : 'previously updated dates'}{' '}
                          {conflict.existingWeekStartDate ?? conflict.weekStartDate}–{conflict.existingWeekEndDate ?? conflict.weekEndDate}
                          {conflict.rows?.length ? ` (Excel row${conflict.rows.length > 1 ? 's' : ''} ${conflict.rows.join(', ')})` : ''}
                          {conflict.existingRows?.length
                            ? ` (also overlaps Excel row${conflict.existingRows.length > 1 ? 's' : ''} ${conflict.existingRows.join(', ')})`
                            : ''}
                        </Typography>
                      </li>
                    ))}
                  </Box>
                )}

                {!!rentUpdateError.unknownRiderPublicIds?.length && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Rider IDs not found: {rentUpdateError.unknownRiderPublicIds.join(', ')}
                  </Typography>
                )}
              </Alert>
            )}

            <Divider />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                disabled={updatingRent}
              >
                Choose Excel file
                <input
                  hidden
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(event) => {
                    handleRentUpdateFileChange(event.target.files?.[0])
                    event.target.value = ''
                  }}
                />
              </Button>
              {rentUpdateFile ? (
                <>
                  <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                    {rentUpdateFile.name} ({(rentUpdateFile.size / (1024 * 1024)).toFixed(2)} MB)
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => handleRentUpdateFileChange(undefined)}
                    disabled={updatingRent}
                  >
                    Remove
                  </Button>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No file selected
                </Typography>
              )}
            </Box>

            {updatingRent && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={22} />
                <Typography variant="body2">Uploading and updating rent amounts…</Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowUpdateRentDialog(false)
              setRentUpdateError(null)
            }}
            disabled={updatingRent}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleRentUpdateUpload}
            disabled={updatingRent || !rentUpdateFile}
          >
            {updatingRent ? 'Updating…' : 'Upload & Update Rent'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Notification */}
      <Snackbar
        open={!!success}
        autoHideDuration={5000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default RiderVehicleHistory